import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import get from 'lodash/get';
import classNames from 'classnames';
import { ComponentClass } from 'conversional-theme';
import { getStyles } from './SingleImageSelectCard.styles';
import Icon from '../icons/Icon';
import { ICONS } from '../icons/IconList';
import ImageWrapper from '../imageWrapper';
import InlineSVGRenderer from '../inlineSVGRenderer';
import { JourneysSelectors, useAppSelector } from 'conversional-journey';
var SelectedOverlay = function (_a) {
    var useClass = _a.useClass, iconContainer = _a.iconContainer;
    var theme = useAppSelector(JourneysSelectors.selectTheme);
    return (_jsx("div", { className: useClass, children: _jsx("div", { className: iconContainer, children: _jsx(Icon, { icon: ICONS.check, size: '6rem', color: theme.colors.brand }) }) }));
};
function renderImage(img, title, theme, isSelected, titleOnImage, svgFillColor, svgStrokeColor, svgBackgroundColor, svgContainerStyles, svgDimensions, mobileSvgWidth, imageStyles, imageContainerStyles, aspectRatio) {
    var _a = getStyles(theme), svgContainer = _a.svgContainer, overlapSvgContainer = _a.overlapSvgContainer, svgImage = _a.svgImage, overlapSvgImage = _a.overlapSvgImage, pixelImage = _a.pixelImage, pixelContainer = _a.pixelContainer, mobilePixelContainer = _a.mobilePixelContainer, selectedIcon = _a.selectedIcon, iconContainer = _a.iconContainer, overlapPixelImage = _a.overlapPixelImage;
    var imageExtraClassName = ComponentClass(imageStyles || {});
    var imageContainerExtraClassName = ComponentClass(imageContainerStyles || {});
    var svgContainerExtraClassName = ComponentClass(svgContainerStyles || {});
    var marker = isSelected ? (_jsx(SelectedOverlay, { useClass: selectedIcon, iconContainer: iconContainer })) : null;
    var pixelContainerClassName = titleOnImage ? mobilePixelContainer : pixelContainer;
    var svgContainerClassName = titleOnImage ? overlapSvgContainer : svgContainer;
    var svgImageClassName = titleOnImage ? overlapSvgImage : svgImage;
    var pixelImageClassName = titleOnImage ? overlapPixelImage : pixelImage;
    var fillColor = svgFillColor ? svgFillColor : theme.colors.brand;
    var strokeColor = svgStrokeColor ? svgStrokeColor : theme.colors.brand;
    var svgBackground = svgBackgroundColor ? svgBackgroundColor : 'transparent';
    return isSVG(img) ? (_jsxs(Box, { className: classNames(svgContainerClassName, svgContainerExtraClassName), sx: {
            backgroundColor: "".concat(svgBackground),
            width: mobileSvgWidth && { xs: mobileSvgWidth, sm: '100%' },
        }, children: [marker, _jsx(InlineSVGRenderer, { svgSrc: img, alt: title, className: svgImageClassName, svgFillColor: svgFillColor, svgStrokeColor: strokeColor, svgDimensions: svgDimensions })] })) : (_jsxs("div", { className: "".concat(pixelContainerClassName, " ").concat(imageContainerExtraClassName), children: [marker, _jsx(ImageWrapper, { src: img, alt: title, aspectRatio: aspectRatio, processWidth: function (width) { return Math.max(width, 250); }, className: "".concat(pixelImageClassName, " ").concat(imageExtraClassName) })] }));
}
function isSVG(url) {
    if (!url)
        return false;
    var extension = url.substr(url.length - 3);
    return extension === 'SVG' || extension === 'svg';
}
var item = {
    hidden: {
        x: 100,
        opacity: 0,
        transition: { duration: 0.15 },
    },
    show: {
        x: 0,
        opacity: 1,
        delay: 300,
        transition: {
            y: { type: 'spring', stiffness: 1000, damping: 15 },
            duration: 0.3,
        },
    },
};
var SingleImageSelectCard = function (_a) {
    var _b, _c;
    var option = _a.option, onClick = _a.onClick, isSelected = _a.isSelected, cardStyles = _a.cardStyles, titleOnImage = _a.titleOnImage, subTitleOnImage = _a.subTitleOnImage, blackBackgroundOnTitle = _a.blackBackgroundOnTitle, svgFillColor = _a.svgFillColor, svgStrokeColor = _a.svgStrokeColor, svgBackgroundColor = _a.svgBackgroundColor, svgContainerStyles = _a.svgContainerStyles, svgDimensions = _a.svgDimensions, avatarOnTopLayout = _a.avatarOnTopLayout, mobileSvgWidth = _a.mobileSvgWidth, imageStyles = _a.imageStyles, imageContainerStyles = _a.imageContainerStyles, aspectRatio = _a.aspectRatio;
    var isDisabled = function () {
        if (typeof option.disabled === 'boolean')
            return option.disabled;
        return get(option, 'disabled.value', false);
    };
    var hasSubOptions = function () { return get(option, 'subOptions', []).length > 0; };
    var hasTitle = function () { return title || subTitle; };
    var theme = useAppSelector(JourneysSelectors.selectTheme);
    var styles = getStyles(theme, { cardHover: !!onClick && !isDisabled() }, mobileSvgWidth);
    var generateTextContainerClassName = function () {
        if (!titleOnImage) {
            return styles.textContainer;
        }
        if (blackBackgroundOnTitle) {
            return "".concat(styles.overlapTextContainer, " ").concat(styles.blackBackground);
        }
        return styles.overlapTextContainer;
    };
    var generateContainerClassName = function () {
        if (!titleOnImage && !mobileSvgWidth && avatarOnTopLayout) {
            return styles.newContainer;
        }
        if (mobileSvgWidth && avatarOnTopLayout) {
            return styles.withMobileSVGContainer;
        }
        if (!titleOnImage) {
            return styles.container;
        }
        if (avatarOnTopLayout) {
            return styles.newOverlapContainer;
        }
        return styles.overlapContainer;
    };
    var titleClassName = blackBackgroundOnTitle ? styles.whiteOverlapTitle : styles.overlapTitle;
    var img = option.img, title = option.title, subTitle = option.subTitle, value = option.value;
    var cardClass = ComponentClass(cardStyles);
    var marker = isSelected ? (_jsx(SelectedOverlay, { useClass: styles.selectedIconMobile, iconContainer: styles.iconContainer })) : null;
    var optionContent = _jsxs(motion.div, { variants: item, className: classNames(generateContainerClassName(), cardClass, styles.genericOptionContainer), onClick: onClick && !isDisabled() && onClick, children: [isDisabled() && _jsx("div", { className: styles.disabledOverlay }), marker, _jsxs("div", { className: classNames((_b = {},
                    _b[styles.imageContainer] = true,
                    _b[styles.imageContainerMobile] = Boolean(mobileSvgWidth),
                    _b)), children: [isDisabled() && (_jsx("span", { className: styles.disabledOverlayText, children: get(option, 'disabled.disabledOverlayText', null) })), renderImage(img, title, theme, isSelected, titleOnImage, svgFillColor, svgStrokeColor, svgBackgroundColor, svgContainerStyles, svgDimensions, mobileSvgWidth, imageStyles || {}, imageContainerStyles || {}, aspectRatio)] }), hasTitle() &&
                (subTitleOnImage ? (_jsxs("div", { className: styles.subTitleOnImageContainer, children: [subTitle && _jsx("h4", { className: styles.subTitleOnImageSubtitle, children: subTitle }), _jsx("p", { className: styles.subTitleOnImageTitle, children: title })] })) : (_jsxs("div", { className: generateTextContainerClassName(), children: [titleOnImage ? (_jsx("p", { className: titleClassName, children: title })) : (_jsx("h3", { className: styles.title, children: title })), subTitle && (subTitle === null || subTitle === void 0 ? void 0 : subTitle.trim()) && _jsx("h4", { className: styles.subTitle, children: subTitle })] })))] }, value || title);
    if (!hasSubOptions())
        return optionContent;
    return (_jsxs("div", { className: classNames((_c = {},
            _c[styles.subOptionParentOptionContainer] = hasSubOptions(),
            _c)), children: [optionContent, _jsx(OptionSubOptions, { styles: styles, option: option, disabled: isDisabled(), onClick: onClick })] }));
};
function OptionSubOptions(_a) {
    var option = _a.option, styles = _a.styles;
    return (_jsx("div", { className: styles.subOptionsContainer, children: (option.subOptions || []).map(function (subOption) { return (_jsx("img", { className: styles.subOptionsImage, src: subOption.img }, subOption.value)); }) }));
}
export default SingleImageSelectCard;
